/**
 * Per-provider "recently selected" AI models, persisted to localStorage.
 * Newest-first, deduped by modelId, capped at CAP. Used by the ModelPicker to
 * put the models an admin actually reaches for at the top of the list.
 */

const CAP = 6;
const KEY = (provider) => `aqrate.recentModels.${provider || 'Bedrock'}`;

// Pure merge — the only non-trivial bit. Kept separate so it's testable without localStorage.
export function mergeRecent(list, entry, cap = CAP) {
  return [entry, ...list.filter((m) => m.modelId !== entry.modelId)].slice(0, cap);
}

export function getRecentModels(provider) {
  try {
    const list = JSON.parse(localStorage.getItem(KEY(provider)) || '[]');
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

export function pushRecentModel(provider, model) {
  if (!model?.modelId) return getRecentModels(provider);
  const entry = {
    modelId: model.modelId,
    modelName: model.modelName || model.modelId,
    vendorName: model.vendorName || '',
    inputPricePerMillionTokens: model.inputPricePerMillionTokens ?? 0,
    outputPricePerMillionTokens: model.outputPricePerMillionTokens ?? 0,
  };
  const next = mergeRecent(getRecentModels(provider), entry);
  try {
    localStorage.setItem(KEY(provider), JSON.stringify(next));
  } catch {
    /* ignore quota / private-mode errors */
  }
  return next;
}

// ponytail: self-check for the dedupe/cap/order logic — run `RECENT_SELFTEST=1 node src/utils/recentModels.js`
// Guarded by `typeof process` so it never runs (or throws) in the browser bundle.
if (typeof process !== 'undefined' && process.env && process.env.RECENT_SELFTEST) {
  const assert = (c, m) => { if (!c) throw new Error('recentModels selftest FAILED: ' + m); };
  const e = (id) => ({ modelId: id, modelName: id });
  let l = [];
  l = mergeRecent(l, e('a'));
  l = mergeRecent(l, e('b'));
  l = mergeRecent(l, e('a')); // re-add a → moves to front, no dupe
  assert(l.length === 2, 'dedupe keeps unique count');
  assert(l[0].modelId === 'a', 're-added item is newest-first');
  let big = [];
  for (let i = 0; i < 10; i++) big = mergeRecent(big, e('m' + i));
  assert(big.length === CAP, `capped at ${CAP}`);
  assert(big[0].modelId === 'm9', 'newest kept at front');
  assert(!big.find((x) => x.modelId === 'm0'), 'oldest evicted past cap');
  // eslint-disable-next-line no-console
  console.log('recentModels selftest OK');
}
